// Round-trip URL search params <-> typed filter object for the planner
// right-pane products picker. Mirrors `@products/features/products-filters`
// but with every key prefixed `source*` so it never collides with the
// global /products page's filters when both are open in parallel tabs.

export type PlannerSourceSearch = {
  sourceSearch?: string;
  sourceCategory?: string[];
  sourceSubcategory?: string[];
  sourceCatalogId?: string[];
  sourceCountry?: string[];
  // Free-text City filter (substring match, server-side ilike). Mirrors the
  // /products page's `city` filter; lives in the popover.
  sourceCity?: string;
  // Free-text Supplier filter (substring match against the product's supplier
  // array, server-side). Mirrors the /products page's `supplierName`.
  sourceSupplierName?: string;
  // Budget tier filter (1..4 == $..$$$$). Restricts to Experience categories
  // server-side. See `packages/shared/src/pricing`.
  sourceValueTier?: number[];
  sourcePage?: number;
  // Rows per page for the picker + slide-down map. Lives in the URL so both
  // mirror the same page window. Default 20 when absent.
  sourcePageSize?: number;
  // Phase 21d — "Find similar" mode. Set to a *productId* by the
  // row-action button; the picker reads this and swaps its data
  // source from `products.list` to `products.findSimilar`. Holding
  // a productId (not the itemId) avoids a second round-trip to
  // resolve item → product in the picker.
  similarTo?: string;
  // Phase 21e — picker source view. `products` (default) shows the
  // products list / filters; `collections` shows saved day-template
  // cards.
  sourceView?: 'products' | 'collections';
  // Collections-mode list controls — separate namespace from the
  // products-side filters so switching views preserves both
  // independently.
  collectionsSearch?: string;
  collectionsCountry?: string[];
  collectionsPage?: number;
  // M5 — custom-region filter. Set from the map's region picker; the
  // products query filters by `customRegionId` so both the picker list and
  // the map narrow to the region (and the map draws its boundary).
  sourceRegionId?: string;
  // Map-viewport bbox (NE/SW corners). Written by the planner map on user
  // pan/zoom and place-search; the picker + map queries filter to products
  // inside it so the list narrows to what's in view. Mutually exclusive with
  // `sourceRegionId` — setting one clears the other.
  sourceNeLat?: number;
  sourceNeLng?: number;
  sourceSwLat?: number;
  sourceSwLng?: number;
};

// Stable list of keys planner-side mutations touch. Helps the validator,
// the merger, and any test that needs to enumerate the namespace.
// Sort fields are intentionally deferred — re-add when the picker
// surfaces a sort affordance.
export const PLANNER_SOURCE_SEARCH_KEYS = [
  'sourceSearch',
  'sourceCategory',
  'sourceSubcategory',
  'sourceCatalogId',
  'sourceCountry',
  'sourceCity',
  'sourceSupplierName',
  'sourceValueTier',
  'sourcePage',
  'sourcePageSize',
  'similarTo',
  'sourceView',
  'collectionsSearch',
  'collectionsCountry',
  'collectionsPage',
  'sourceRegionId',
  'sourceNeLat',
  'sourceNeLng',
  'sourceSwLat',
  'sourceSwLng',
] as const;

const asStringArray = (v: unknown): string[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string')
    : undefined;

const asFiniteNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

// Budget tiers from the URL — keep only the four valid integers (1..4).
const asValueTier = (v: unknown): number[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const tiers = v.filter(
    (x): x is number => typeof x === 'number' && x >= 1 && x <= 4
  );
  return tiers.length > 0 ? tiers : undefined;
};

const PAGE_SIZE_VALUES: ReadonlyArray<number> = [10, 20, 40, 60];

export const validatePlannerSourceSearch = (
  input: Record<string, unknown>
): PlannerSourceSearch => ({
  sourceSearch:
    typeof input.sourceSearch === 'string' ? input.sourceSearch : undefined,
  sourceCategory: asStringArray(input.sourceCategory),
  sourceSubcategory: asStringArray(input.sourceSubcategory),
  sourceCatalogId: asStringArray(input.sourceCatalogId),
  sourceCountry: asStringArray(input.sourceCountry),
  sourceCity:
    typeof input.sourceCity === 'string' ? input.sourceCity : undefined,
  sourceSupplierName:
    typeof input.sourceSupplierName === 'string'
      ? input.sourceSupplierName
      : undefined,
  sourceValueTier: asValueTier(input.sourceValueTier),
  sourcePage:
    typeof input.sourcePage === 'number' ? input.sourcePage : undefined,
  sourcePageSize:
    typeof input.sourcePageSize === 'number' &&
    PAGE_SIZE_VALUES.includes(input.sourcePageSize)
      ? input.sourcePageSize
      : undefined,
  // Loose UUID check (no version constraint) so hand-crafted URLs that
  // smuggle a non-UUID string get dropped at the FE boundary instead
  // of round-tripping to the BE just to fail its `z.uuid()` gate.
  similarTo:
    typeof input.similarTo === 'string' &&
    /^[0-9a-f-]{36}$/i.test(input.similarTo)
      ? input.similarTo
      : undefined,
  sourceView:
    input.sourceView === 'collections' || input.sourceView === 'products'
      ? input.sourceView
      : undefined,
  collectionsSearch:
    typeof input.collectionsSearch === 'string'
      ? input.collectionsSearch
      : undefined,
  collectionsCountry: asStringArray(input.collectionsCountry),
  collectionsPage:
    typeof input.collectionsPage === 'number'
      ? input.collectionsPage
      : undefined,
  sourceRegionId:
    typeof input.sourceRegionId === 'string' &&
    /^[0-9a-f-]{36}$/i.test(input.sourceRegionId)
      ? input.sourceRegionId
      : undefined,
  sourceNeLat: asFiniteNumber(input.sourceNeLat),
  sourceNeLng: asFiniteNumber(input.sourceNeLng),
  sourceSwLat: asFiniteNumber(input.sourceSwLat),
  sourceSwLng: asFiniteNumber(input.sourceSwLng),
});
